import { PaymentInitializationError } from "./utils/errors/PaymentInitializationError";
import { createPaymentLogger } from "./utils/payment-logger";
import PaymentHandler from "./handlers/PaymentHandler";

// eslint-disable-next-line import/no-cycle
import RegularPaymentHandler from "./handlers/RegularPaymentHandler/RegularPaymentHandler";
import AffirmPaymentHandler from "./handlers/AffirmPaymentHandler/AffirmPaymentHandler";
import FreePaymentHandler from "./handlers/FreePaymentHandler/FreePaymentHandler";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "./services/useBasketPaymentService";
import {
  shouldUseAffirmHandler,
  shouldUseFreeHandler,
  shouldUseWaitlistHandler,
} from "./utils/handlers/handlers";
import useInvoicePaymentService from "./services/useInvoicePaymentService";
import WaitlistPaymentHandler from "./handlers/WaitlistPaymentHandler/WaitlistPaymentHandler";
import { type PaymentManagerConfig } from "./PaymentManager";

const log = createPaymentLogger("PaymentHandlerFactory");

/**
 * A factory responsible for selecting a PaymentHandler based on
 * the basket's (or more broadly, the environment's) characteristics.
 *
 * We use the singleton pattern for factories to be consistent with the PaymentGatewayFactory, where
 * the use of the singleton pattern is more important. See PaymentGatewayFactory for more details.
 */
class PaymentHandlerFactory {
  private static handlerInstances = new Map<string, PaymentHandler>();

  static getOrCreateHandler(
    config: PaymentManagerConfig,
    basket: PaymentServiceBasket,
    services: {
      basketPaymentService: ReturnType<typeof useBasketPaymentService>;
      invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
    }
  ): PaymentHandler {
    const HandlerClass = PaymentHandlerFactory.getHandlerClass(basket);
    const handlerKey = PaymentHandlerFactory.buildHandlerKey(
      HandlerClass.handlerName,
      basket
    );

    let handler = PaymentHandlerFactory.handlerInstances.get(handlerKey);

    if (!handler) {
      log.info(`Creating new handler instance for ${handlerKey}`);
      handler = new HandlerClass(config, basket, services);

      if (handler === undefined) {
        throw new PaymentInitializationError(
          `Failed to create payment handler instance for ${handlerKey}. Handler initialization returned undefined.`,
          {
            metadata: {
              handlerKey,
            },
          }
        );
      }

      PaymentHandlerFactory.handlerInstances.set(handlerKey, handler);
    } else {
      log.info(`Using existing handler instance for ${handlerKey}`);
    }

    if (handler === undefined) {
      throw new PaymentInitializationError(
        `Payment handler instance is undefined for ${handlerKey}. Unable to process payment.`,
        {
          metadata: {
            handlerKey,
            category: "Payment Handler Factory",
          },
        }
      );
    }

    return handler;
  }

  /**
   * Builds a composite cache key that includes the handler class name and the
   * basket's gateway identity. This ensures that when the basket's payment
   * gateway changes (e.g. Yuno → Stripe after cart item changes), a fresh
   * handler is created with the correct gateway instead of returning a stale
   * cached handler bound to the previous gateway.
   */
  private static buildHandlerKey(
    handlerName: string,
    basket: PaymentServiceBasket
  ): string {
    const gatewayType = basket.paymentGateway?.gateway_type ?? "none";
    const publishableKey = basket.paymentGateway?.publishable_api_key ?? "none";
    const currency =
      basket.paymentGateway?.currency ?? basket.currency ?? "none";
    return `${handlerName}:${gatewayType}:${publishableKey}:${currency}`;
  }

  private static getHandlerClass(basket: PaymentServiceBasket) {
    if (shouldUseWaitlistHandler(basket)) {
      return WaitlistPaymentHandler;
    }

    if (shouldUseFreeHandler(basket)) {
      return FreePaymentHandler;
    }

    if (shouldUseAffirmHandler(basket)) {
      return AffirmPaymentHandler;
    }

    // Everything else should use our Regular handler
    return RegularPaymentHandler;
  }
}

export default PaymentHandlerFactory;
